import { Component, OnInit } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { Founder } from "../../../models/founder";
import { FoundersService } from "src/app/services/founders.service";
import { StartupsService } from "src/app/services/startups.service";
import { COUNTRIES } from "../../constants/constants";

@Component({
  standalone: true,
  selector: "app-founders-form",
  templateUrl: "./founders-form.component.html",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    ToastModule,
  ],
  providers: [MessageService],
})
export class FoundersFormComponent implements OnInit {
  founderForm!: FormGroup;
  id?: number;
  submitted = false;
  isSaving = false;
  currentUser: any;
  startupsList: any[] = [];
  countriesList: any[] = COUNTRIES;
  roleOptions = [
    "Founder",
    "Co-Founder",
    "CEO",
    "CTO",
    "COO",
    "CPO",
    "CMO",
    "Advisor",
    "Board Member",
    "Investor",
  ];

  constructor(
    private fb: FormBuilder,
    private foundersService: FoundersService,
    private startupsService: StartupsService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private _location: Location,
  ) {
    this.currentUser = JSON.parse(localStorage.getItem("currentUser") || "[]");
  }

  ngOnInit(): void {
    this.buildForm();
    this.getStartups();

    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.id = +id;
      this.loadFounder(this.id);
    }

    this.founderForm.get("name")?.valueChanges.subscribe((value: string) => {
      if (this.id) return;
      const generated = (value || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
      this.founderForm.get("slug")?.setValue(generated, { emitEvent: false });
    });
  }

  private buildForm(): void {
    this.founderForm = this.fb.group({
      id: [""],
      name: ["", Validators.required],
      slug: ["", Validators.required],
      headline: [""],
      biography: [""],
      location: [""],
      country: [""],
      website: [""],
      twitter: [""],
      linkedin: [""],
      github: [""],
      producthunt: [""],
      instagram: [""],
      status: [1],
      companies: this.fb.array([]),
      exits: this.fb.array([]),
      investments: this.fb.array([]),
      interviews: this.fb.array([]),
      articles: this.fb.array([]),
      startup_timeline: this.fb.array([]),
    });
  }

  getStartups(): void {
    this.startupsService
      .list({
        _start: 1,
        _limit: 300,
        _sort_field: "startup_name",
        _sort_order: "asc",
      })
      .subscribe((data: any) => (this.startupsList = data ?? []));
  }

  get f() {
    return this.founderForm.controls;
  }

  get companies(): FormArray {
    return this.founderForm.get("companies") as FormArray;
  }
  get exits(): FormArray {
    return this.founderForm.get("exits") as FormArray;
  }
  get investments(): FormArray {
    return this.founderForm.get("investments") as FormArray;
  }
  get interviews(): FormArray {
    return this.founderForm.get("interviews") as FormArray;
  }
  get articles(): FormArray {
    return this.founderForm.get("articles") as FormArray;
  }
  get startupTimeline(): FormArray {
    return this.founderForm.get("startup_timeline") as FormArray;
  }

  addCompany(data: any = {}): void {
    this.companies.push(
      this.fb.group({
        startup_id: [data.id ?? data.startup_id ?? "", Validators.required],
        role: [data.role || "", Validators.required],
        year: [data.year || ""],
      }),
    );
  }

  addExit(data: any = {}): void {
    this.exits.push(
      this.fb.group({
        company: [data.company || "", Validators.required],
        acquirer: [data.acquirer || ""],
        year: [data.year || ""],
        amount: [data.amount || ""],
        description: [data.description || ""],
      }),
    );
  }

  addInvestment(data: any = {}): void {
    this.investments.push(
      this.fb.group({
        company: [data.company || "", Validators.required],
        year: [data.year || ""],
        stage: [data.stage || ""],
        amount: [data.amount || ""],
        url: [data.url || ""],
      }),
    );
  }

  addInterview(data: any = {}): void {
    this.interviews.push(
      this.fb.group({
        title: [data.title || "", Validators.required],
        source: [data.source || ""],
        date: [data.date || ""],
        url: [data.url || ""],
      }),
    );
  }

  addArticle(data: any = {}): void {
    this.articles.push(
      this.fb.group({
        title: [data.title || "", Validators.required],
        source: [data.source || ""],
        date: [data.date || ""],
        url: [data.url || ""],
      }),
    );
  }

  addTimelineItem(data: any = {}): void {
    this.startupTimeline.push(
      this.fb.group({
        year: [data.year || ""],
        title: [data.title || "", Validators.required],
        description: [data.description || ""],
      }),
    );
  }

  remove(array: FormArray, index: number): void {
    array.removeAt(index);
  }

  private loadFounder(id: number): void {
    this.foundersService.getId(id).subscribe((res: any) => {
      const parse = (value: any) => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        try {
          return JSON.parse(value);
        } catch {
          return [];
        }
      };

      this.founderForm.patchValue({
        id: res.id,
        name: res.name,
        slug: res.slug,
        headline: res.headline,
        biography: res.biography,
        location: res.location,
        country: res.country,
        website: res.website,
        twitter: res.twitter,
        linkedin: res.linkedin,
        github: res.github,
        producthunt: res.producthunt,
        instagram: res.instagram,
        status: res.status ?? 1,
      });

      parse(res.exits).forEach((e: any) => this.addExit(e));
      parse(res.investments).forEach((i: any) => this.addInvestment(i));
      parse(res.interviews).forEach((i: any) => this.addInterview(i));
      parse(res.articles).forEach((a: any) => this.addArticle(a));
      parse(res.startup_timeline).forEach((t: any) => this.addTimelineItem(t));
      parse(res.companies).forEach((c: any) => this.addCompany(c));
    });
  }

  goback(): void {
    this._location.back();
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    this.founderForm.markAllAsTouched();
    if (this.founderForm.invalid) {
      this.messageService.add({
        severity: "warn",
        summary: "Missing information",
        detail:
          "Please fill in all required fields (marked with *) before saving.",
      });
      return;
    }

    this.isSaving = true;
    const value = this.founderForm.value;

    const formData = new FormData();
    const scalarFields = [
      "name",
      "slug",
      "headline",
      "biography",
      "location",
      "country",
      "website",
      "twitter",
      "linkedin",
      "github",
      "producthunt",
      "instagram",
      "status",
    ];
    scalarFields.forEach((field) => formData.append(field, value[field] ?? ""));

    const companies = (value.companies ?? []).map((c: any) => {
      const startup = this.startupsList.find((s) => s.id === c.startup_id);
      return {
        id: c.startup_id,
        startup_name: startup?.startup_name ?? "",
        page_url: startup?.page_url ?? "",
        role: c.role ?? "",
        year: c.year ?? "",
      };
    });

    formData.append("companies", JSON.stringify(companies));
    formData.append("exits", JSON.stringify(value.exits ?? []));
    formData.append("investments", JSON.stringify(value.investments ?? []));
    formData.append("interviews", JSON.stringify(value.interviews ?? []));
    formData.append("articles", JSON.stringify(value.articles ?? []));
    formData.append(
      "startup_timeline",
      JSON.stringify(value.startup_timeline ?? []),
    );
    formData.append("user_id", this.currentUser?.user_id ?? "");

    try {
      if (this.id) {
        await firstValueFrom(this.foundersService.update(formData, this.id));
        this.messageService.add({
          severity: "success",
          summary: "Saved",
          detail: "Founder updated successfully",
        });
      } else {
        const res: any = await firstValueFrom(
          this.foundersService.create(formData),
        );
        if (res?.id) {
          this.id = res.id;
          this.founderForm.patchValue({ id: res.id });
        }
        this.messageService.add({
          severity: "success",
          summary: "Created",
          detail: "Founder created successfully",
        });
      }
    } catch {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Unable to save founder. Please try again.",
      });
    } finally {
      this.isSaving = false;
    }
  }
}
